import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert AI agent deployment coach evaluating a learner's deploy-and-iterate work.

Score each criterion 1-4 (1=Missing, 2=Partial, 3=Solid, 4=Excellent).

DEPLOYMENT CHECKLIST: Are all 8 items confirmed before the agent runs unsupervised? (1-4)

FAILURE IDENTIFICATION: Is the root cause of the real-world failure correctly identified? (1-4)

FIX QUALITY: Is the fix specific, exact, and does it address only the identified failure? (1-4)

RETEST COVERAGE: Were all three tests run after the fix with documented results? (1-4)

AGENT LOG COMPLETENESS: Are all 6 fields complete with a change log entry? (1-4)

After scoring:
1. Give a total score out of 20.
2. Is there a second failure in the checklist that hasn't been addressed yet? Name it.
3. Review the known limitations field: is anything missing that would surprise someone running this agent?
4. Is the change log specific enough that someone could understand the history of this agent in 2 years?

Here is my work:

[PASTE YOUR CHECKLIST STATUS, FIX DOCUMENTATION, RETEST RESULTS, AND AGENT LOG]`;

const RUBRIC_ROWS = [
  { criterion: 'DEPLOYMENT CHECKLIST', missing: '3 or fewer boxes checked', partial: '4-5 boxes checked', solid: '6-7 boxes checked', excellent: 'All 8 boxes confirmed before agent runs unsupervised' },
  { criterion: 'FAILURE IDENTIFICATION', missing: 'No failure named', partial: 'Vague description of what went wrong', solid: 'Failure pattern named correctly', excellent: 'Specific instruction or guardrail identified as root cause' },
  { criterion: 'FIX QUALITY', missing: 'No fix written', partial: 'General improvement stated', solid: 'Specific change described', excellent: 'Exact revised wording with clear reasoning' },
  { criterion: 'RETEST COVERAGE', missing: 'No retests run', partial: 'Original scenario only', solid: 'Original scenario plus standard test', excellent: 'All three tests run after fix with results documented' },
  { criterion: 'AGENT LOG COMPLETENESS', missing: '2 or fewer fields', partial: '3-4 fields complete', solid: '5 fields complete', excellent: 'All 6 fields complete including change log entry for today' },
];

const CHECKLIST_ITEMS = [
  'The trigger fires correctly on the expected condition, confirmed by testing, not assumed.',
  'Every instruction step produces the correct output before passing to the next step.',
  'All tools are authenticated and have the correct access type (read/write/send).',
  'The output lands in the right destination and the right person sees it first.',
  'At least two guardrail conditions are active and confirmed working.',
  'The escalation logic fires correctly on the defined condition.',
  'The break test was run. I know what happens when the input is bad or incomplete.',
  'Someone other than me can read the spec and understand what this agent does.',
];

const FAILURE_PATTERNS = ['Vague instructions', 'Missing guardrail', 'Tool access mismatch', 'Trigger too broad', 'Output destination wrong'];

const LOG_FIELDS = [
  { num: '01', name: 'Agent Name + Version', key: 'log_name', hint: 'Start at v1.0. Increment (v1.1, v1.2...) every time the spec changes.', height: 60 },
  { num: '02', name: 'Purpose', key: 'log_purpose', hint: 'One sentence. What does this agent do and why does it exist?', height: 60 },
  { num: '03', name: 'Current Spec Summary', key: 'log_spec', hint: 'Trigger, key instructions (summary), tools, output destination, guardrails. Update when anything changes.', height: 100 },
  { num: '04', name: 'Change Log', key: 'log_changes', hint: 'Date | What changed | Why. One line per change. Start today.', height: 80 },
  { num: '05', name: 'Known Limitations', key: 'log_limits', hint: 'What does this agent NOT handle? What inputs will produce incorrect results? Be honest.', height: 70 },
  { num: '06', name: 'Owner', key: 'log_owner', hint: 'Your name. Your contact. Who takes ownership if you change roles?', height: 60 },
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60 }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function RubricTable({ rows }) {
  return (
    <div style={s.rubricTable}>
      <div style={s.rubricHeader}>
        {['CRITERION', '1 — MISSING', '2 — PARTIAL', '3 — SOLID', '4 — EXCELLENT'].map(h => (
          <div key={h} style={s.rubricCol0}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={s.rubricRow}>
          <div style={{ ...s.rubricCol0, background: '#F2F0EC', color: '#1B2A4A', fontWeight: '700' }}>{row.criterion}</div>
          <div style={s.rubricCol}>{row.missing}</div>
          <div style={s.rubricCol}>{row.partial}</div>
          <div style={s.rubricCol}>{row.solid}</div>
          <div style={s.rubricCol}>{row.excellent}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

function calcTotal(data, keys) {
  return keys.reduce((sum, k) => sum + (parseInt(data[k]) || 0), 0);
}

export default function WorkbookDay8({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day8'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day8: { ...(existing.progress?.day8 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day8: { ...(existing.progress?.day8 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day8: { ...(existing.progress?.day8 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rubricTotal = calcTotal(data, ['score_checklist', 'score_failure', 'score_fix', 'score_retest', 'score_log']);

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 8 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={8} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 8</div>
        <div style={s.headerTitle}>Deploy and Iterate</div>
        <div style={s.headerDesc}>One fix at a time. One test at a time. That's how agents compound.</div>
      </div>

      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>
          An agent you deployed once is a snapshot. An agent you iterate is a system that compounds. The iteration loop is four steps: Observe (run and log everything), Identify (name the specific failure, not "it broke"), Fix (one change at a time), Retest (the failing scenario first, then all three tests). The five common failure patterns are vague instructions, missing guardrail, tool access mismatch, trigger too broad, and output destination wrong. The Agent Log turns a working agent into a documented organizational asset: versioned, owned, and maintainable by someone other than you.
        </div>
      </div>

      <Section title="Part 1 — Deployment Checklist">
        <div style={s.exerciseIntro}>Check every box before your agent runs unsupervised. Any unchecked box is a fix item for today.</div>
        <div style={s.checklist}>
          {CHECKLIST_ITEMS.map((item, i) => (
            <div key={i} style={{ ...s.checkItem, borderBottom: i < CHECKLIST_ITEMS.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <input
                type="checkbox"
                style={{ marginTop: '2px', flexShrink: 0 }}
                checked={data[`ck_${i}`] || false}
                onChange={e => set(`ck_${i}`, e.target.checked)}
              />
              <span style={{ fontSize: '13px', color: '#3D3D3D', lineHeight: '1.6' }}>{item}</span>
            </div>
          ))}
        </div>
        <Field label="Boxes I can't check yet (and why):" value={data.ck_notes} onChange={v => set('ck_notes', v)} minHeight={60} />
      </Section>

      <Section title="Part 2 — Apply Your Fix">
        <div style={s.exerciseIntro}>Use your real-world observation from the between-session run. One failure. One fix. Follow the loop.</div>

        <div style={s.partHeader}>STEP 1 — IDENTIFY THE FAILURE PATTERN</div>
        <Field label="What I observed in the real-world run:" value={data.observed} onChange={v => set('observed', v)} minHeight={66} />
        <div style={s.field}>
          <div style={s.fieldLabel}>Which failure pattern does this match?</div>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '8px' }}>
            {FAILURE_PATTERNS.map(opt => (
              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#3D3D3D', cursor: 'pointer' }}>
                <input type="radio" name="failure_pattern" checked={data.failure_pattern === opt} onChange={() => set('failure_pattern', opt)} /> {opt}
              </label>
            ))}
          </div>
        </div>

        <div style={s.partHeader}>STEP 2 — WRITE THE SPECIFIC FIX</div>
        <div style={s.fixTable}>
          {[
            { key: 'fix_original', name: 'Original Text', hint: 'The exact wording of the instruction or guardrail as it currently reads.', height: 70 },
            { key: 'fix_revised', name: 'Revised Text', hint: 'The exact new wording that fixes the failure.', height: 70 },
            { key: 'fix_why', name: 'Why This Fix', hint: "One sentence: what does the revised version know or prevent that the original didn't?", height: 60 },
          ].map((row, i, arr) => (
            <div key={row.key} style={{ ...s.fixRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.fixLabel}>
                <div style={s.fixLabelName}>{row.name}</div>
                <div style={s.fixLabelHint}>{row.hint}</div>
              </div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: row.height, background: '#FAF8F5' }}
                value={data[row.key] || ''}
                onChange={e => set(row.key, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div style={s.partHeader}>STEP 3 — RETEST RESULTS</div>
        <div style={s.retestGrid}>
          <div style={{ ...s.retestCol, borderRight: '1px solid #E8E6E2' }}>
            <div style={s.retestHeader}>Scenario That Failed</div>
            <div style={s.retestBody}>
              {['Fixed', 'Still failing', 'New issue'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#3D3D3D', cursor: 'pointer' }}>
                  <input type="radio" name="rt_fail" checked={data.rt_fail === opt} onChange={() => set('rt_fail', opt)} /> {opt}
                </label>
              ))}
            </div>
          </div>
          <div style={{ ...s.retestCol, borderRight: '1px solid #E8E6E2' }}>
            <div style={s.retestHeader}>Standard Test</div>
            <div style={s.retestBody}>
              {['Pass', 'Fail'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#3D3D3D', cursor: 'pointer' }}>
                  <input type="radio" name="rt_std" checked={data.rt_std === opt} onChange={() => set('rt_std', opt)} /> {opt}
                </label>
              ))}
            </div>
            <div style={{ padding: '0 4px 4px' }}>
              <textarea style={{ ...s.textarea, minHeight: 44 }} placeholder="What happened:" value={data.rt_std_notes || ''} onChange={e => set('rt_std_notes', e.target.value)} />
            </div>
          </div>
          <div style={s.retestCol}>
            <div style={s.retestHeader}>Edge Case / Break Test</div>
            <div style={s.retestBody}>
              {['Pass', 'Fail'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#3D3D3D', cursor: 'pointer' }}>
                  <input type="radio" name="rt_edge" checked={data.rt_edge === opt} onChange={() => set('rt_edge', opt)} /> {opt}
                </label>
              ))}
            </div>
            <div style={{ padding: '0 4px 4px' }}>
              <textarea style={{ ...s.textarea, minHeight: 44 }} placeholder="What happened:" value={data.rt_edge_notes || ''} onChange={e => set('rt_edge_notes', e.target.value)} />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Part 3 — Your Agent Log">
        <div style={s.exerciseIntro}>Fill in all six fields right now. This document lives with your agent. Update it every time anything changes.</div>
        <div style={s.logBlock}>
          <div style={s.logTitle}>Agent Log</div>
          {LOG_FIELDS.map((field, i, arr) => (
            <div key={field.key} style={{ ...s.logRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.logLabel}>
                <div style={s.logNum}>{field.num}</div>
                <div style={s.logName}>{field.name}</div>
                <div style={s.logHint}>{field.hint}</div>
              </div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: field.height, background: '#FAF8F5' }}
                value={data[field.key] || ''}
                onChange={e => set(field.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Scoring Criteria">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>
          Use these criteria to evaluate your deployment readiness and iteration quality.
        </div>
        <RubricTable rows={RUBRIC_ROWS} />
        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your work:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Checklist" value={data.score_checklist} onChange={v => set('score_checklist', v)} />
          <ScoreField label="Failure ID" value={data.score_failure} onChange={v => set('score_failure', v)} />
          <ScoreField label="Fix" value={data.score_fix} onChange={v => set('score_fix', v)} />
          <ScoreField label="Retest" value={data.score_retest} onChange={v => set('score_retest', v)} />
          <ScoreField label="Agent Log" value={data.score_log} onChange={v => set('score_log', v)} />
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{rubricTotal} / 20</div>
          </div>
        </div>
      </Section>

      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Paste this with your completed checklist, fix documentation, retest results, and Agent Log.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy AI Scoring Prompt'}</button>
      </div>

      <Section title="Scores from AI Evaluation">
        <Field label="AI Score: ___ / 20     Second unchecked checklist item to address:" value={data.ai_score} onChange={v => set('ai_score', v)} minHeight={66} />
      </Section>

      <CalloutBox title="THE STANDARD">
        Keep working until all 8 checklist items are confirmed, the fix is applied and retested, and the Agent Log has all 6 fields complete including a change log entry for today. If any box is still unchecked, that's your fix item before the session ends.
      </CalloutBox>

      <Section title="Reflection">
        <Field
          label="1. Which of the five failure patterns did your real-world run reveal? Does that pattern tell you something about how you designed the agent originally: what assumption you made that turned out to be wrong?"
          value={data.reflection_1}
          onChange={v => set('reflection_1', v)}
          minHeight={80}
        />
        <Field
          label="2. Look at your Agent Log. Is there a colleague or team member who could maintain this agent if you weren't available for two weeks? What would they need to know that isn't yet in the log?"
          value={data.reflection_2}
          onChange={v => set('reflection_2', v)}
          minHeight={80}
        />
      </Section>

      <Section title="Wins Tracker — Day 8">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <CalloutBox title="BEFORE DAY 9 — YOUR ASSIGNMENT">
        <span>Run your agent three more times on real work. For each run:</span>
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Log the run in your change log: date, what triggered it, what the output was.</li>
          <li>Note any new edge case or failure that appeared.</li>
          <li>If a new failure appeared: apply one fix using the iteration loop, retest, and log the change.</li>
          <li>Come to Day 9 knowing your agent's current version number and the status of any open issues.</li>
        </ol>
      </CalloutBox>

      <div style={{ ...s.section, marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <div style={s.fieldLabel}>After 3 runs, my agent version is:</div>
            <input type="text" style={{ ...s.textarea, resize: 'none', minHeight: 'unset', padding: '9px 11px' }} value={data.agent_version || ''} onChange={e => set('agent_version', e.target.value)} />
          </div>
          <div style={{ flex: 2, minWidth: '220px' }}>
            <div style={s.fieldLabel}>Open issues:</div>
            <input type="text" style={{ ...s.textarea, resize: 'none', minHeight: 'unset', padding: '9px 11px' }} value={data.open_issues || ''} onChange={e => set('open_issues', e.target.value)} />
          </div>
        </div>
      </div>

      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 8 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 8 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 8 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it if you need to come back.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '18px' },
  partHeader: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', background: '#F2F0EC', padding: '10px 14px', marginBottom: '12px', borderLeft: '3px solid #D4A847' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  checklist: { border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  checkItem: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px' },
  fixTable: { border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  fixRow: { display: 'flex' },
  fixLabel: { width: '140px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  fixLabelName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  fixLabelHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.5' },
  retestGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  retestCol: {},
  retestHeader: { background: '#1B2A4A', color: '#D4A847', padding: '9px 11px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase' },
  retestBody: { padding: '10px 11px', background: '#FAF8F5', display: 'flex', flexDirection: 'column', gap: '7px' },
  logBlock: { border: '1.5px solid #E8E6E2' },
  logTitle: { background: '#1B2A4A', color: '#D4A847', padding: '8px 16px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' },
  logRow: { display: 'flex' },
  logLabel: { width: '140px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  logNum: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A', marginBottom: '3px' },
  logName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
  logHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.4' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
